import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import * as yup from 'yup';
import moment from "moment";
import { applicationContext } from "helpers/services";


interface IContext {
    search: ISearchRequest
    recordCount: number
    userGroups: IUserGroup[]
    isLoading: boolean
    isSaving: boolean
    userGroup: UserGroup

    handleChangePage: (page: number) => void
    searchUserGroups: (searchRequest: ISearchRequest) => Promise<void>
    getUserGroup: (id?: string) => Promise<void>
    saveUserGroup: (userGroup: UserGroup) => Promise<boolean>
    deleteUserGroup: (userId: string) => Promise<void>
}

interface ISearchRequest {
    pageNr: number
    pageSize: number
}

interface IUserGroup {
    id: string
    key: string
    description: string
    lastUpdatedBy: string
    lastUpdatedOn: moment.Moment
}

export let userGroupSchema = yup.object().shape({
    id: yup.string().default(''),
    key: yup.string().default(''),
    description: yup.string().default('')
})

type UserGroup = yup.InferType<typeof userGroupSchema>;

export let userGroupsContext = React.createContext({} as IContext);

let { Provider } = userGroupsContext;

let UserGroupsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let history = useHistory();
    let location = useLocation();

    let appService = useContext(applicationContext);

    let [isLoading, setLoading] = useState(true);
    let [isSaving, setSaving] = useState(false);

    let [search, setSearch] = useState<ISearchRequest>({
        pageNr: 0,
        pageSize: 15
    });

    let [userGroups, setUserGroups] = useState<IUserGroup[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [userGroup, setUserGroup] = useState<UserGroup>(userGroupSchema.getDefault());


    useEffect(() => {
        if (location.state)
            processState(location.state);
    }, [location.state])

    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: { search } }} />


    let processState = (state: any) => {
        if (state.search) {
            setSearch(state.search);
            searchUserGroups(state.search);
        }
    }

    let doSearch = (values: ISearchRequest) => {
        let request = {
            ...values,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        history.push(location.pathname, { search: request });
    }

    let searchUserGroups = async (searchRequest: ISearchRequest) => {
        try {
            setLoading(true);
            let response = await axios.get('/api/UserGroup/GetUserGroups', { params: searchRequest });
            setUserGroups(response.data.items);
            setTotalRecords(response.data.recordCount);
        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        history.push(location.pathname, { search: request });
    }

    let getUserGroup = async (id?: string) => {
        if (id === undefined) {
            setUserGroup(userGroupSchema.getDefault())
            return;
        }

        try {
            let res = await axios.get('/api/UserGroup/GetUserGroup', { params: { id } });
            setUserGroup(res.data.item);
        } catch (err) { }
    }

    let saveUserGroup = async (userGroup: UserGroup) => {
        try {
            setSaving(true);
            await axios.post('/api/UserGroup/SaveUserGroup', { ...userGroup });
            await appService.getSimecReferenceData();

            doSearch(search);
            setSaving(false);
            return true;
        } catch (err) {
            return false;
        }
    }

    let deleteUserGroup = async (id: string) => {
        if (window.confirm("Sicuro di voler eliminare questo gruppo?")) {
            try {
                await axios.delete(`/api/UserGroup/DeleteUserGroup/${id}`)
                setUserGroups(userGroups.filter(group => group.id !== id));
                setTotalRecords(recordCount - 1);
                await appService.getSimecReferenceData();
                enqueueSnackbar("Gruppo eliminato", { variant: 'success' });
            } catch (error) { }
        }
    }


    return (
        <Provider value={{
            search,
            recordCount,
            userGroups,
            isLoading,
            isSaving,
            userGroup,

            handleChangePage,
            searchUserGroups,
            getUserGroup,
            saveUserGroup,
            deleteUserGroup
        }}>
            {children}
        </Provider>
    );
};

export default UserGroupsProvider;